import Avatar from '@mui/material/Avatar'
import AvatarGroup from '@mui/material/AvatarGroup'
import Button from '@mui/material/Button'
import { PPDialog } from 'components/PPDialog'
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { composerPostEditSelector, composerSelectedTikTokProfilesSelector, tiktokPostSelector } from 'services/compose/selectors'
import { TikTokPostProfileSettings, TikTokPrivacyOptions } from 'shared'
import styles from './TikTokOptions.pcss'
import { TikTokProfileSettings } from './TikTokProfileSettings'

type ComposerTikTokPostOptionsProps = {
  isPhotoPost: boolean,
  settings?: Record<string, TikTokPostProfileSettings>,
  popupMode?: boolean,
  actionsClassName?: string,
  singleAction?: boolean,
  options?: Record<string, TikTokPrivacyOptions>,
  /** Per-profile "Remember this choice" state, for callers that fetched the settings themselves. */
  hasSavedSettings?: Record<string, boolean>,
  skipFetchSettings?: boolean,
  onChange?: (settings?: TikTokPostProfileSettings) => void
}

const AVATAR_SIZE = { width: 24, height: 24 }
const MAX_AVATARS = 3

export function ComposerTikTokPostOptions({
  settings, popupMode, actionsClassName, isPhotoPost, singleAction, options, hasSavedSettings, skipFetchSettings, onChange
}: ComposerTikTokPostOptionsProps) {
  const profiles = useSelector(composerSelectedTikTokProfilesSelector)
  const post = useSelector(tiktokPostSelector)
  const isEditing = Boolean(useSelector(composerPostEditSelector))
  const [popupOpen, setPopupOpen] = useState(false)
  // Mount the dialog lazily on first open, then keep it mounted so reopening does not refetch every profile
  const [popupMounted, setPopupMounted] = useState(false)

  const openSettingsPopup = useCallback(() => {
    setPopupMounted(true)
    setPopupOpen(true)
  }, [])

  const closeSettingsPopup = useCallback(() => {
    setPopupOpen(false)
  }, [])

  const profilesIcon = useMemo(() => (
    <AvatarGroup max={MAX_AVATARS}>
      {profiles.map(profile => (
        <Avatar key={profile.id} alt={profile.name} src={profile.image} sx={AVATAR_SIZE} />
      ))}
    </AvatarGroup>
  ), [profiles])

  const profileSettings = settings || post.settings || {}

  const actions = singleAction ? (
    <Button startIcon={profilesIcon} onClick={openSettingsPopup}>TikTok Settings</Button>
  ) : (
    <div className={`${styles['actions-box']} ${actionsClassName || ''} `}>
      <Button startIcon={profilesIcon} onClick={openSettingsPopup}>Privacy</Button>
      <Button startIcon={profilesIcon} onClick={openSettingsPopup}>Allow</Button>
      <Button startIcon={profilesIcon} onClick={openSettingsPopup}>Promotion video</Button>
    </div>
  )

  return (
    <div className={styles.wrapper} data-testid="tiktok-post-options">
      {profiles.length === 1 && !popupMode && (
        <TikTokProfileSettings
          isPhotoPost={isPhotoPost}
          key={profiles[0].ppid}
          profile={profiles[0]}
          settings={profileSettings[profiles[0].ppid]}
          options={options && options[profiles[0].ppid]}
          hasSavedSettings={hasSavedSettings && hasSavedSettings[profiles[0].ppid]}
          ignoreSavedSettings={isEditing}
          skipFetchSettings={skipFetchSettings}
          showLoader
          onChange={onChange}
        />
      )}
      {(popupMode || profiles.length > 1) && (
        <>
          {actions}
          {popupMounted && (
            <PPDialog open={popupOpen} onClose={closeSettingsPopup} size="lg" title="TikTok settings" keepMounted>
              <div className={styles.popup}>
                {profiles.map(profile => (
                  <div key={profile.id} className={styles['settings-box']}>
                    <TikTokProfileSettings
                      key={profile.ppid}
                      isPhotoPost={isPhotoPost}
                      profile={profile}
                      settings={profileSettings[profile.ppid]}
                      options={options && options[profile.ppid]}
                      hasSavedSettings={hasSavedSettings && hasSavedSettings[profile.ppid]}
                      showLoader
                      skipFetchSettings={skipFetchSettings}
                      ignoreSavedSettings={isEditing}
                      className={styles['popup-settings']}
                      onChange={onChange}
                    />
                  </div>
                ))}
              </div>
            </PPDialog>
          )}
        </>
      )}
    </div>
  )
}
